/* eslint-disable react/prop-types */
import {
  Box,
  Divider,
  Grid,
  Paper,
  Avatar,
  Button,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import moment from "moment";

// Icons
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import WcOutlinedIcon from "@mui/icons-material/WcOutlined";
import StarsIcon from "@mui/icons-material/Stars";
import { getInitials } from "../../../../member/memberDetails/utlis";
import Loader from "../../../../../components/common/Loader/Loader";
import { PERMISSIONS } from "../../../../../contants";
import { ROUTES } from "../../../../../utils/constants";
import { SET_SELECTED_FRIEND } from "../../../../../redux/actionTypes/dashboard";

function DetailItem({ icon, label, value, xs = 10, sm = 5, md = 2, lg = 2 }) {
  return (
    <Grid item xs={xs} sm={sm} md={md} lg={lg}>
      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
        <Box sx={{ mr: 1.5, color: "#7a869a", mt: 0.2, display: "flex" }}>
          {React.cloneElement(icon, { fontSize: "medium" })}
        </Box>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: "#7a869a",
              display: "block",
              mb: 0.5,
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            {label}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: "#183053",
              wordBreak: "break-word",
              fontSize: "0.875rem",
            }}
          >
            {value || "-"}
          </Typography>
        </Box>
      </Box>
    </Grid>
  );
}

export default function FriendDetailCard({
  friendInfo,
  hasPermission,
  breadcrumbs,
}) {
  const {
    friendId,
    fullName,
    mobileNumber,
    emailId,
    nationalityName,
    addressLine1,
    cityName,
    pincode,
    stateName,
    introducerName,
    introducerEmail,
    introducerMobileNumber,
    dateOfBirth,
    professionName,
    createdAt,
    divisionName,
    subDivisionName,
    genderName,
    universityName,
    collegeName,
    profilePhotoUrl,
  } = friendInfo;

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const onEditClick = () => {
    setIsLoading(true);
    setTimeout(() => {
      dispatch({ type: SET_SELECTED_FRIEND, payload: friendInfo });
      navigate(`${ROUTES.FRIEND_EDIT_PROFILE.path}/${friendId}`);
      setIsLoading(false);
    }, 1000);
  };

  const addressString = [
    addressLine1,
    cityName,
    stateName,
    pincode,
    nationalityName,
  ]
    .filter(Boolean)
    .join(", ");

  const introducerString = [
    introducerName,
    introducerMobileNumber,
    introducerEmail,
  ]
    .filter(Boolean)
    .join(" / ");

  return (
    <Box sx={{ width: "100%", mb: 2 }}>
      {/* 1. Blue Banner (Matches Member Details styling) */}
      <Box
        sx={{
          backgroundColor: "#3fa9f5",
          minHeight: "48px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 3,
          py: 1.5,
          position: "relative",
        }}
      >
        <Box /> {/* Spacer */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
            justifyContent: "flex-end",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "#fff",
              fontWeight: 500,
              textAlign: "right",
              fontSize: "13px",
            }}
          >
            {breadcrumbs}
          </Typography>
          {hasPermission(PERMISSIONS.UPDATE_FRIEND) && (
            <Button
              variant="contained"
              size="small"
              sx={{
                backgroundColor: "#fff",
                color: "#183053",
                "&:hover": { backgroundColor: "#f0f0f0" },
                textTransform: "none",
                fontWeight: 600,
                minWidth: "60px",
                padding: "4px 16px",
                borderRadius: "4px",
                boxShadow: "none",
              }}
              onClick={onEditClick}
            >
              Edit
            </Button>
          )}
        </Box>
      </Box>

      {/* 2. Main Profile Info Card */}
      <Paper
        sx={{
          p: 3,
          pt: 0,
          borderRadius: "0",
          position: "relative",
          boxShadow: "0px 2px 8px rgba(0,0,0,0.05)",
          border: "1px solid #e0e0e0",
          borderTop: "none",
        }}
      >
        <Box
          sx={{ display: "flex", flexDirection: { xs: "column", md: "row" } }}
        >
          {/* Left Col: Avatar & ID */}
          <Box
            sx={{
              width: { xs: "100%", md: "180px" },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mt: { xs: "-40px", md: "-50px" },
              mb: { xs: 2, md: 0 },
            }}
          >
            <Avatar
              src={profilePhotoUrl}
              sx={{
                width: 130,
                height: 130,
                border: "4px solid #fff",
                backgroundColor: "#f5f6fa",
                color: "#183053",
                fontSize: "44px",
                fontWeight: 600,
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              }}
            >
              {!profilePhotoUrl && getInitials(fullName)}
            </Avatar>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mt: 2,
                color: "#555",
              }}
            >
              <BadgeOutlinedIcon
                fontSize="small"
                sx={{ mr: 1, color: "#7a869a" }}
              />
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "#183053" }}
              >
                Friend ID: {friendId || "-"}
              </Typography>
            </Box>
          </Box>

          {/* Right Col: Basic Summary Details */}
          <Box sx={{ flex: 1, pl: { xs: 0, md: 4 }, pt: { xs: 2, md: 3 } }}>
            <Typography
              variant="h5"
              sx={{
                color: "#183053",
                fontWeight: 700,
                mb: 1.5,
                fontSize: "22px",
              }}
            >
              {fullName}
            </Typography>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mb: 2 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", color: "#555" }}
              >
                <MailOutlineIcon
                  fontSize="small"
                  sx={{ mr: 1, color: "#183053" }}
                />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, color: "#183053" }}
                >
                  {emailId || "-"}
                </Typography>
              </Box>
              <Box
                sx={{ display: "flex", alignItems: "center", color: "#555" }}
              >
                <PhoneOutlinedIcon
                  fontSize="small"
                  sx={{ mr: 1, color: "#183053" }}
                />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, color: "#183053" }}
                >
                  {mobileNumber || "-"}
                </Typography>
              </Box>
              {divisionName && (
                <Box
                  sx={{ display: "flex", alignItems: "center", color: "#555" }}
                >
                  <StarsIcon
                    fontSize="small"
                    sx={{ mr: 1, color: "#183053" }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, color: "#183053" }}
                  >
                    {divisionName}
                  </Typography>
                </Box>
              )}
            </Box>

            <Box
              sx={{ display: "flex", alignItems: "flex-start", color: "#555" }}
            >
              <LocationOnOutlinedIcon
                fontSize="small"
                sx={{ mr: 1, color: "#183053", mt: 0.2 }}
              />
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, color: "#183053" }}
              >
                {addressString || "-"}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 4, borderColor: "#f0f0f0" }} />

        {/* 3. Detailed Data Grid (Bottom Portion) */}

        <Grid container spacing={3} columns={10}>
          {/* --- Line 1 (Pure 5 Items) --- */}
          <DetailItem
            icon={<CalendarTodayOutlinedIcon />}
            label="Friend Since"
            value={createdAt ? moment(createdAt).format("MMMM, YYYY") : "-"}
          />
          <DetailItem
            icon={<PersonOutlineOutlinedIcon />}
            label="Date of Birth"
            value={dateOfBirth ? moment(dateOfBirth).format("DD-MM-YYYY") : "-"}
          />
          <DetailItem
            icon={<WorkOutlineOutlinedIcon />}
            label="Profession"
            value={professionName}
          />
          <DetailItem
            icon={<BusinessOutlinedIcon />}
            label="Division / Sub Division"
            value={[divisionName, subDivisionName].filter(Boolean).join(" / ")}
          />
          <DetailItem
            icon={<WcOutlinedIcon />}
            label="Gender"
            value={genderName}
          />

          {/* --- Line 2 (Introduced By को 2 स्लॉट (lg=4) + बाकी 3 आइटम्स = 10) --- */}
          <DetailItem
            icon={<PersonOutlineOutlinedIcon />}
            label="Introduced By / Mob. / Email"
            value={introducerString}
            xs={10}
            sm={10}
            md={4}
            lg={4}
          />
          <DetailItem
            icon={<SchoolOutlinedIcon />}
            label="University"
            value={universityName}
          />
          <DetailItem
            icon={<SchoolOutlinedIcon />}
            label="College"
            value={collegeName}
          />
          <DetailItem
            icon={<PublicOutlinedIcon />}
            label="Nationality"
            value={nationalityName}
          />
        </Grid>
      </Paper>

      {isLoading && <Loader />}
    </Box>
  );
}
